/**
 * A location's time zone on the edit form (Site Host Terms 3.4: time-of-use
 * periods are read in the Site's local time). The API refuses a time-of-use
 * electricity rate without a place zone; these helpers let the form say so
 * before Save. Pure, unit-tested in siteTimeZone.test.ts.
 */
import { isSiteZone, resolveTimeZoneFromAddress } from '@evi/shared';

/** The zones EV Initiative sites are in, west to east. A stored zone outside it is still offered. */
export const SITE_TIME_ZONES: ReadonlyArray<{ zone: string; label: string }> = [
  { zone: 'Pacific/Honolulu', label: 'Hawaii (Pacific/Honolulu)' },
  { zone: 'America/Anchorage', label: 'Alaska (America/Anchorage)' },
  { zone: 'America/Vancouver', label: 'Pacific, British Columbia (America/Vancouver)' },
  { zone: 'America/Los_Angeles', label: 'Pacific (America/Los_Angeles)' },
  { zone: 'America/Phoenix', label: 'Arizona, no daylight saving (America/Phoenix)' },
  { zone: 'America/Edmonton', label: 'Mountain, Alberta (America/Edmonton)' },
  { zone: 'America/Denver', label: 'Mountain (America/Denver)' },
  { zone: 'America/Regina', label: 'Saskatchewan (America/Regina)' },
  { zone: 'America/Winnipeg', label: 'Central, Manitoba (America/Winnipeg)' },
  { zone: 'America/Chicago', label: 'Central (America/Chicago)' },
  { zone: 'America/Toronto', label: 'Eastern, Ontario (America/Toronto)' },
  { zone: 'America/New_York', label: 'Eastern (America/New_York)' },
  { zone: 'America/Halifax', label: 'Atlantic (America/Halifax)' },
  { zone: 'America/St_Johns', label: 'Newfoundland (America/St_Johns)' },
];

export function timeZoneOptions(current: string | null | undefined): Array<{ zone: string; label: string }> {
  if (!current || SITE_TIME_ZONES.some(z => z.zone === current)) return [...SITE_TIME_ZONES];
  return [{ zone: current, label: isSiteZone(current) ? current : `${current} (not accepted: choose a place)` }, ...SITE_TIME_ZONES];
}

/** The address's zone when its region has exactly one (or the longitude settles a split one). */
export function suggestedTimeZone(address: {
  state?: string | null; country?: string | null; coordinates?: { lat?: number | null; lng?: number | null } | null;
}): string | null {
  const r = resolveTimeZoneFromAddress(address);
  return r.confidence === 'certain' && isSiteZone(r.timezone) ? r.timezone : null;
}

/** Whether the rate the form would save reads a time-of-use period. */
export function rateNeedsTimeZone(input: {
  mode: 'tariff' | 'inline_tou' | 'flat'; inlineWindows: number;
  tariff: { type: string; rates?: { energy_rate?: number | null; time_of_use_schedule?: unknown[] } } | null;
}): boolean {
  if (input.mode === 'inline_tou') return input.inlineWindows > 0;
  if (input.mode === 'tariff' && input.tariff) {
    return input.tariff.type === 'time_of_use' && (input.tariff.rates?.time_of_use_schedule?.length ?? 0) > 0;
  }
  return false;
}

/**
 * What the form does about a missing zone, matching the API: it refuses a
 * time-of-use rate without a place zone only when the save changes the rate
 * settings or the zone. Saving something else (a rename) is allowed, with a
 * warning, because the API allows it.
 */
export function timeZoneSaveState(input: {
  needsZone: boolean; zone: string; rateChanged: boolean; zoneChanged: boolean;
}): 'ok' | 'warn' | 'block' {
  if (!input.needsZone || isSiteZone(input.zone)) return 'ok';
  return input.rateChanged || input.zoneChanged ? 'block' : 'warn';
}

export { isSiteZone };
